import type { PayRecord } from '../types';
import type { NurseBandPositionResolvedRates, NurseBandResolvedRates } from '../services/api';

function weekdayTotalForSchedule(
  rates: NurseBandPositionResolvedRates | NurseBandResolvedRates,
  scheduleId?: number | null
): number | null {
  const schedule =
    scheduleId != null ? rates.schedules?.find((s) => s.scheduleId === scheduleId) : undefined;
  const day =
    schedule?.dayTypes.find((d) => d.dayType === 'Mon–Fri')
    ?? rates.baseRates?.find((d) => d.dayType === 'Mon–Fri');
  return day && day.totalRate > 0 ? day.totalRate : null;
}

function resolvePositionRates(
  rates: NurseBandResolvedRates,
  positionId?: number | null
): NurseBandPositionResolvedRates | NurseBandResolvedRates | null {
  if (positionId != null && positionId > 0 && rates.positions?.length) {
    const match = rates.positions.find((p) => p.positionId === positionId);
    if (match) return match;
  }
  if (rates.positions?.length === 1) return rates.positions[0];
  return rates;
}

/**
 * Labels for the single pay-amount field (form `baseRate`): meaning follows
 * payment type + payment frequency.
 */
export interface PayRateBaseFieldCopy {
  label: string;
  hint?: string;
}

export function getPayRateBaseFieldLabel(
  paymentTypeName: string | undefined,
  paymentFrequencyName: string | undefined
): PayRateBaseFieldCopy {
  const pt = (paymentTypeName || '').trim();
  const pf = (paymentFrequencyName || '').trim();

  if (pt === 'Hourly') {
    return { label: 'Hourly rate' };
  }
  if (pt === 'Daily') {
    return { label: 'Daily rate' };
  }
  if (pt === 'Salary') {
    if (pf === 'Monthly') {
      return { label: 'Monthly gross' };
    }
    if (pf === 'Weekly') {
      return { label: 'Weekly gross' };
    }
    if (pf === 'Fortnightly') {
      return { label: 'Fortnightly gross' };
    }
    return { label: 'Pay rate' };
  }
  if (pt === 'Project') {
    if (pf === 'Contract') {
      return { label: 'Contract / project total' };
    }
    if (pf === 'Monthly Invoice') {
      return { label: 'Monthly invoice amount' };
    }
    return { label: 'Project pay' };
  }

  return { label: 'Pay rate' };
}

export function formatPayListAmount(
  record: PayRecord,
  nurseBandingResolvedRates?: NurseBandResolvedRates | null
): string {
  if (nurseBandingResolvedRates) {
    const positionRates = resolvePositionRates(nurseBandingResolvedRates, record.positionId);
    if (positionRates?.bandYear != null) {
      const weekday = weekdayTotalForSchedule(positionRates, record.scheduleId);
      if (weekday != null) return `${weekday.toFixed(2)} (banding)`;
    }
  }
  const n = record.baseRate ?? 0;
  if (n == null || Number(n) === 0) return 'N/A';
  return Number(n).toFixed(2);
}

export function weekdayTotalForScheduleFromRates(
  rates: NurseBandResolvedRates | null | undefined,
  scheduleId?: number | null,
  positionId?: number | null
): number | null {
  if (!rates) return null;
  const positionRates = resolvePositionRates(rates, positionId);
  if (!positionRates || positionRates.bandYear == null) return null;
  return weekdayTotalForSchedule(positionRates, scheduleId);
}
